import { RuleGroup } from "@monolith-forensics/monolith-ui";
import {
  UseQueryResult,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";

export enum FileStatus {
  ACTIVE = "active",
  ARCHIVED = "archived",
  UPLOADING = "uploading",
  FAILED = "failed",
}

// case_docs.source_type values the app branches on (monolith-api
// FILE_SOURCE_TYPES).
export const FILE_SOURCE_TYPES = {
  EVIDENCE_PHOTO: "evidence_photo",
  EVIDENCE_ROOT_FOLDER: "evidence_root_folder",
  EVIDENCE_ITEM_FOLDER: "evidence_item_folder",
} as const;

export type File = {
  file_id: number;
  uuid: string;
  name: string;
  path?: string;
  size: number;
  ext: string;
  file_key?: string;
  description: string | null;
  created_on: string;
  client_modified?: string;
  is_folder: boolean;
  status: FileStatus;
  link: string;
  source_type?: string | null;
  source_id?: number | null;
  parent: {
    uuid: string;
  } | null;
  md5?: string;
  sha1?: string;
  sha256?: string;
  case: {
    uuid: string;
    case_id: number;
    case_name: string;
    case_number: string;
  } | null;
  created_by: {
    user_id: number;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
};

export type FileWithPath = {
  file: globalThis.File;
  path: string;
};

export type FileLog = {
  uuid: string;
};

export type GetFilesQuery = {
  uuid?: string;
  case_uuid?: string;
  parent_uuid?: string;
  // Managed row lookups (ENG-2156): an evidence item's folder is
  // `evidence_item_folder` with its evidence id as `source_id`.
  source_type?: string;
  source_id?: number;
  page?: number;
  pageSize?: number;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  filter?: RuleGroup;
  search?: string;
};

export type GetFilesResponse = {
  data: File[];
  path: {
    uuid: string;
    name: string;
    path_order: number;
  }[];
  nextPage: number | null;
  page: number;
};

export type GetFilesFn = (q: GetFilesQuery) => Promise<GetFilesResponse>;

export type GetFilePathQuery = { uuid: string };

export type GetFilePathResponse = {
  data: {
    uuid: string;
    name: string;
    path_order: number;
    // Set on the managed Evidence Files rows (ENG-1446): an item folder is
    // `evidence_item_folder` with its evidence id as `source_id`.
    source_type?: string | null;
    source_id?: number | null;
  }[];
};

export type UseGetFilePath = (
  q: GetFilePathQuery,
  options?: UseQueryOptions<GetFilePathResponse, Error>
) => UseQueryResult<GetFilePathResponse>;

export type GetFilePathFn = (
  q: GetFilePathQuery
) => Promise<GetFilePathResponse>;

export type CreateFileData = {
  uuid: string;
  name: string;
  size?: number; // bytes
  ext?: string;
  path?: string;
  client_modified?: string;
  is_folder?: boolean;
  file_key?: string;
  description?: string;
  parent_uuid?: string;
  case_uuid?: string;
  md5?: string;
  sha1?: string;
  sha256?: string;
  status?: FileStatus;
  source_type?: string | null;
  source_id?: number | null;
};

export type CreateFileResponse = {
  data: File;
};

export type CreateFileFn = (
  data: CreateFileData
) => Promise<CreateFileResponse>;

export type BulkCreateFilesData = {
  files: CreateFileData[];
};

export type BulkCreateFilesResponse = {
  data: File[];
};

export type BulkCreateFilesFn = (
  data: BulkCreateFilesData
) => Promise<BulkCreateFilesResponse>;

export type UseGetFiles = (
  q: GetFilesQuery,
  options?: Partial<UseQueryOptions<GetFilesResponse, Error>>
) => UseQueryResult<GetFilesResponse>;

export type UseGetInfiniteFiles = (
  q: GetFilesQuery,
  options?: {
    enabled?: boolean;
  }
) => UseInfiniteQueryResult<{
  pages: GetFilesResponse[];
  pageParams: number[];
}>;

export type UseCreateCaseFile = (
  options?: UseMutationOptions<CreateFileResponse, Error, CreateFileData>
) => UseMutationResult<CreateFileResponse, Error, CreateFileData>;

export type UpdateFileData = {
  uuid: string;
  name?: string;
  description?: string;
  parent_uuid?: string;
  status?: FileStatus;
  md5?: string;
  sha1?: string;
  sha256?: string;
};

export type UpdateFileResponse = {
  data: File;
};

export type UpdateFileFn = (
  data: UpdateFileData
) => Promise<UpdateFileResponse>;

export type UseUpdateCaseFile = (
  options?: UseMutationOptions<UpdateFileResponse, Error, UpdateFileData>
) => UseMutationResult<UpdateFileResponse, Error, UpdateFileData>;

export type StartMultipartUploadData = {
  uuid: string;
  parts: { PartNumber: number }[];
  file: CreateFileData;
};

export type StartMultipartUploadResult = {
  success?: boolean;
  data: {
    signedUrls: string[];
    uploadId: string;
    fileKey?: string;
    uuid?: string;
  };
};

export type StartMultipartUploadFn = (
  data: StartMultipartUploadData
) => Promise<StartMultipartUploadResult>;

export type CompleteMultipartUploadData = {
  uuid: string;
  uploadId: string;
  parts: { PartNumber: number; ETag: string }[];
} & UpdateFileData;

export type CompleteMultipartUploadResult = {
  message: string;
};

export type CompleteMultipartUploadFn = (
  data: CompleteMultipartUploadData
) => Promise<CompleteMultipartUploadResult>;

export type DeleteFileData = {
  uuid: string;
};

export type DeleteFileResponse = {
  message: string;
  data: File[];
  // Set to false by httpDelete's handleError when the request fails.
  success?: boolean;
};

export type DeleteFileFn = (
  data: DeleteFileData
) => Promise<DeleteFileResponse>;

export type UseDeleteCaseFile = (
  options?: UseMutationOptions<DeleteFileResponse, Error, DeleteFileData>
) => UseMutationResult<DeleteFileResponse, Error, DeleteFileData>;

export type GetFileLogsQuery = {
  uuid: string;
};

export type GetFileLogsResponse = {
  data: FileLog[];
};

export type GetFileLogsFn = (
  q: GetFileLogsQuery
) => Promise<GetFileLogsResponse>;

export type UseGetFileLogs = (
  q: GetFileLogsQuery,
  options?: UseQueryOptions<GetFileLogsResponse, Error>
) => UseQueryResult<GetFileLogsResponse>;
